// client/src/services/lexistarAssets.ts
//
// LexiStar asset register + D26 asset ownership API client (ST-00188, T1-S08 /
// T3-S01). Server: server/modules/lexistar/fleet (/api/lexistar/assets,
// /api/lexistar/asset-ownership). Shapes: src/types/lexistar-assets.ts.
//
// Thin wrapper over the shared axios instance in `services/api` so the page
// inherits the cookie-session auth interceptors, CSRF header and single-flight
// 401 refresh. `api` already carries `/api` in its baseURL. Every helper
// resolves to the response BODY. Asset and alias payloads use the DB column
// names (snake_case); the transfer payload is { assetId, toTenantId, effectiveFrom,
// acknowledgeBackdatedTrips, plannedTripIds }.
//
// ST-00289 follow-ups: getTransferPreview (what confirming a transfer does: trackers that move,
// planned trips it cancels, trips under way, a backdated date's trips). Converted from
// lexistarAssets.js in the same change (CLAUDE.md TypeScript rule); every export is unchanged.

import api from './api';
import type {
  AssetOwnershipHistory,
  LexistarAssetList,
  LexistarAssetSummary,
  ListAssetsParams,
  OwnershipTransferResult,
  TransferPreview,
  TransferPreviewParams,
  TransferRequest,
  TransferTenant,
} from '../types/lexistar-assets';

const ASSETS = '/lexistar/assets';
const OWNERSHIP = '/lexistar/asset-ownership';

/** Drop undefined / null / '' so the API never sees `q=` or `tenantId=`. */
const clean = <T extends object>(params?: T): Partial<T> =>
  Object.fromEntries(
    Object.entries(params || {}).filter(([, v]) => v !== undefined && v !== null && v !== '')
  ) as Partial<T>;

const body = <T>(res: { data: T }): T => res.data;

/** An asset's alias row (GET /assets/:id, POST /assets/:id/aliases). */
export interface AssetAlias {
  id: string;
  asset_id: string;
  kind: string;
  value: string;
  value_normalised: string;
  source_system: string | null;
  effective_from: string | null;
  effective_to: string | null;
  created_at: string;
  created_by: string | null;
}

/** An asset or alias body: the register's DB column names. */
export type AssetBody = Record<string, unknown>;

/**
 * Visible assets, paged: { assets, pagination: { page, limit, total, totalPages },
 * summary: { byType } }. Each asset carries access_type ('Transporter' = your
 * organisation owns it; 'Admin' = global-admin view of another tenant's asset).
 */
export const listAssets = (params?: ListAssetsParams): Promise<LexistarAssetList> =>
  api.get<LexistarAssetList>(ASSETS, { params: clean(params) }).then(body);

/** { asset, aliases, ownership } */
export const getAsset = (
  id: string
): Promise<{
  asset: LexistarAssetSummary;
  aliases: AssetAlias[];
  ownership: AssetOwnershipHistory['ownership'];
}> => api.get(`${ASSETS}/${id}`).then(body);

/** { asset } — 409 { error, existingAssetId } on a duplicate registration. */
export const createAsset = (data: AssetBody): Promise<{ asset: LexistarAssetSummary }> =>
  api.post(ASSETS, data).then(body);

/** { asset } — send `version` for optimistic concurrency (409 when stale). */
export const updateAsset = (
  id: string,
  data: AssetBody
): Promise<{ asset: LexistarAssetSummary }> => api.patch(`${ASSETS}/${id}`, data).then(body);

/** Soft delete: { deleted: { id, registration, aliasesRemoved } } */
export const deleteAsset = (
  id: string
): Promise<{ deleted: { id: string; registration: string; aliasesRemoved: number } }> =>
  api.delete(`${ASSETS}/${id}`).then(body);

/** { alias } */
export const addAlias = (id: string, data: AssetBody): Promise<{ alias: AssetAlias }> =>
  api.post(`${ASSETS}/${id}/aliases`, data).then(body);

export const removeAlias = (id: string, aliasId: string): Promise<{ deleted: { id: string } }> =>
  api.delete(`${ASSETS}/${id}/aliases/${aliasId}`).then(body);

/** POST /assets/import-from-devices */
export interface ImportFromDevicesResult {
  tenant_id: number;
  created: number;
  alreadyRegistered: number;
  registrations: number;
  devicesWithRegistration: number;
  assets: Array<{ id: string; registration: string; asset_type: string }>;
}

/**
 * One asset per device-registry registration that has none yet (idempotent):
 * { created, alreadyRegistered, registrations, devicesWithRegistration, assets }.
 */
export const importFromDevices = (
  data: { tenant_id?: number | null } = {}
): Promise<ImportFromDevicesResult> =>
  api.post(`${ASSETS}/import-from-devices`, clean(data)).then(body);

/** { asset, ownership } — non-admins see only their own organisation's periods. */
export const getOwnershipHistory = (assetId: string): Promise<AssetOwnershipHistory> =>
  api.get<AssetOwnershipHistory>(OWNERSHIP, { params: { assetId } }).then(body);

/**
 * What confirming the transfer would do (ST-00289): { trackers, planned_trips, underway_trips,
 * backdated, cancellation_reason, from, to, … }. Same checks as the transfer itself.
 */
export const getTransferPreview = (params: TransferPreviewParams): Promise<TransferPreview> =>
  api.get<TransferPreview>(`${OWNERSHIP}/transfer-preview`, { params: clean(params) }).then(body);

/**
 * Transfer to another organisation. effectiveFrom: 'YYYY-MM-DD' (midnight SAST),
 * omitted = now. Not in the future. plannedTripIds: the preview's planned trips (cancelled);
 * acknowledgeBackdatedTrips: true when the preview asked for it. 409 { code } when either no
 * longer matches the truck (reload the preview).
 */
export const transferOwnership = (data: TransferRequest): Promise<OwnershipTransferResult> =>
  api.post<OwnershipTransferResult>(`${OWNERSHIP}/transfer`, clean(data)).then(body);

/** Active organisations for the transfer picker (existing tenants API; global admins only). */
export const listTransferTenants = (): Promise<TransferTenant[]> =>
  api.get<{ data?: TransferTenant[] } | TransferTenant[]>('/tenants/current/all').then((res) => {
    const d = res.data;
    if (Array.isArray(d)) return d;
    return d?.data || [];
  });
